import React from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '../context/AppContext'
import { 
  Heart, 
  Brain, 
  Eye, 
  Shield, 
  Activity, 
  Zap,
  ArrowRight,
  CheckCircle,
  Star,
  Users,
  Camera,
  FileText
} from 'lucide-react'

function HomePage() {
  const { isAuthenticated, user } = useApp()

  const features = [
    {
      icon: Heart,
      title: 'Cardiovascular Health',
      description: 'Heart rate, blood pressure, HRV analysis',
      color: 'text-red-500'
    },
    {
      icon: Activity,
      title: 'Body Composition',
      description: 'BMI, body fat, metabolic age assessment',
      color: 'text-blue-500'
    },
    {
      icon: Shield,
      title: 'Skin Health',
      description: 'Hydration, elasticity, wrinkle analysis',
      color: 'text-green-500'
    },
    {
      icon: Eye,
      title: 'Eye Health',
      description: 'Fatigue, redness, digital strain detection',
      color: 'text-purple-500'
    },
    {
      icon: Brain,
      title: 'Mental Wellness',
      description: 'Stress levels, mood, sleep quality',
      color: 'text-yellow-500'
    },
    {
      icon: Zap,
      title: 'Nutritional Indicators',
      description: 'Hydration, vitamin deficiency risks',
      color: 'text-orange-500'
    }
  ]

  const healthCategories = [
    'Cardiovascular Health',
    'Body Composition', 
    'Skin Health',
    'Eye Health',
    'Mental Wellness',
    'Nutritional Indicators',
    'Aging & Longevity',
    'Lifestyle Indicators',
    'Facial Structure',
    'Respiratory Health',
    'Immune System',
    'Hormonal Balance',
    'Toxin Exposure'
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-logo text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              FaceScan AI
            </h1>
            <p className="text-lg mb-6 text-white/90">
              Comprehensive Health Analysis from a Single Face Scan
            </p>
            <p className="text-base mb-8 text-white/80 max-w-2xl mx-auto">
              Get detailed insights into your cardiovascular health, skin condition, 
              eye health, mental wellness, and more using advanced AI technology.
            </p>
            
            {isAuthenticated ? (
              <div className="space-y-4">
                <p className="text-lg">Welcome back, {user?.name || 'User'}!</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <Link 
                    to="/scan" 
                    className="inline-flex items-center bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md"
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    Face Scan
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                  <Link 
                    to="/hra" 
                    className="inline-flex items-center bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md"
                  >
                    <FileText className="w-5 h-5 mr-2" />
                    HRA Assessment
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link 
                  to="/scan" 
                  className="inline-flex items-center bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Face Scan
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <Link 
                  to="/hra" 
                  className="inline-flex items-center bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  HRA Assessment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Options Section - Show for all users */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Choose Your Health Assessment
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Select the type of health analysis you'd like to perform
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Face Scan Option */}
            <Link 
              to="/scan"
              className="group bg-gradient-to-br from-primary-50 to-secondary-50 rounded-xl p-6 border-2 border-primary-200 hover:border-primary-400 transition-all duration-300 hover:shadow-lg"
            >
              <div className="flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Camera className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
                Face Scan Analysis
              </h3>
              <p className="text-gray-600 text-sm mb-4 text-center">
                Get comprehensive health insights from a single face scan using advanced AI technology
              </p>
              <div className="flex items-center justify-center text-primary-600 font-semibold group-hover:text-primary-700">
                <span>Start Scan</span>
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            {/* HRA Option */}
            <Link 
              to="/hra"
              className="group bg-gradient-to-br from-secondary-50 to-accent-50 rounded-xl p-6 border-2 border-secondary-200 hover:border-secondary-400 transition-all duration-300 hover:shadow-lg"
            >
              <div className="flex items-center justify-center w-16 h-16 bg-secondary-600 rounded-full mx-auto mb-4 group-hover:scale-110 transition-transform">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
                Health Risk Assessment
              </h3>
              <p className="text-gray-600 text-sm mb-4 text-center">
                Complete a comprehensive questionnaire to assess your health risks and get personalized recommendations
              </p>
              <div className="flex items-center justify-center text-secondary-600 font-semibold group-hover:text-secondary-700">
                <span>Start Assessment</span>
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={`py-12 ${isAuthenticated ? 'bg-gray-50' : 'bg-white'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              13 Health Categories Analyzed
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our AI analyzes your face to extract comprehensive health metrics 
              across multiple wellness categories
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center hover:shadow-md transition-shadow">
                <feature.icon className={`w-8 h-8 mx-auto mb-3 ${feature.color}`} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Complete Health Analysis
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-w-4xl mx-auto">
              {healthCategories.map((category, index) => (
                <div key={index} className="flex items-center p-2 bg-gray-50 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-xs text-gray-700">{category}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              How It Works
            </h2>
            <p className="text-gray-600">
              Simple, fast, and comprehensive health analysis in 3 easy steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Take a Photo
              </h3>
              <p className="text-gray-600 text-sm">
                Position your face in the camera frame and take a clear photo. 
                Our AI will guide you for optimal positioning.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-secondary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                AI Analysis
              </h3>
              <p className="text-gray-600 text-sm">
                Our advanced AI analyzes your facial features to extract 
                comprehensive health metrics across 13 categories.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-accent-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Get Results
              </h3>
              <p className="text-gray-600 text-sm">
                Receive detailed health insights, personalized recommendations, 
                and download a comprehensive PDF report.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Why Choose FaceScan AI?
            </h2>
            <p className="text-gray-600">
              Advanced technology meets comprehensive health analysis
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-success text-white rounded-full flex items-center justify-center mx-auto mb-3">
                <Star className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                100+ Metrics
              </h3>
              <p className="text-gray-600 text-sm">
                Comprehensive analysis across all major health categories
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-3">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Instant Results
              </h3>
              <p className="text-gray-600 text-sm">
                Get detailed health insights in under 60 seconds
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-secondary-600 text-white rounded-full flex items-center justify-center mx-auto mb-3">
                <FileText className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                PDF Reports
              </h3>
              <p className="text-gray-600 text-sm">
                Download comprehensive health reports for your records
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-neutral-600 text-white rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Privacy First
              </h3>
              <p className="text-gray-600 text-sm">
                Your data is secure and images are automatically deleted
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-logo text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-4">
              Ready to Discover Your Health?
            </h2>
            <p className="text-lg mb-6 text-white/90">
              Join thousands of users who have gained valuable health insights
            </p>
            {isAuthenticated ? (
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link 
                  to="/scan" 
                  className="inline-flex items-center bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Face Scan Analysis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <Link 
                  to="/hra" 
                  className="inline-flex items-center bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Health Risk Assessment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link 
                  to="/scan" 
                  className="inline-flex items-center bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Face Scan Analysis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <Link 
                  to="/hra" 
                  className="inline-flex items-center bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Health Risk Assessment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-8 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-base font-semibold text-gray-900 mb-3">
              Important Medical Disclaimer
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed">
              FaceScan AI is NOT a medical device or diagnostic tool. All health measurements 
              are estimates for wellness and informational purposes only. Results should not 
              replace professional medical advice, diagnosis, or treatment. Always consult 
              healthcare professionals for any health concerns.
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}

export default HomePage
